import { MessagingReducerState } from '../types/redux/messagingState';
import { MessagingActions } from '../types/redux/messagingActions';
import { AUTH, MESSAGING } from 'core/datasets/action';
import { Message } from 'messaging/types/messages';
import { generateChannelsFromMap } from 'messaging/utils/messagingUtils';

const initState: MessagingReducerState = {
    channels: {},
    channelsInLoad: false,
    newMessage: '',
    currentChannelId: null,
    previousChannelId: null,
    token: null,
    hasUnreadMessages: false,
    channelsMap: null,
    newChatCreated: null,
    channelsMapByAgreement: {},
    assistantConversationId: null,
    assistantConversationName: null,
    assistantConversationMuted: null,
};

function filterChannelMessages(msg: Message) {
    return msg.channelId && (Boolean(msg.text?.trim().length) || Boolean(msg.files?.length));
}

export const messagingReducer = (state = initState, action: MessagingActions): MessagingReducerState => {
    switch (action.type) {
        case MESSAGING.REQUEST_CHANNELS:
            return {
                ...state,
                channelsInLoad: true,
            };

        case MESSAGING.CHANGE_CHANNEL_FIELD: {
            const { field, channelId, value } = action.payload ?? {};
            return {
                ...state,
                channels: {
                    ...state.channels,
                    [channelId]: {
                        ...state.channels[channelId],
                        [field]: value,
                    },
                },
            };
        }

        case MESSAGING.SELECT_CHANNEL:
            return {
                ...state,
                currentChannelId: action.payload,
                previousChannelId: state.currentChannelId,
                assistantConversationId: null,
                assistantConversationName: null,
                assistantConversationMuted: null,
            };

        case MESSAGING.CHANGE_NEW_MESSAGE:
            return { ...state, newMessage: action.payload };

        case MESSAGING.ADD_CHANNEL: {
            const { id, messages } = action.payload ?? {};

            return {
                ...state,
                channelsInLoad: false,
                channels: {
                    ...state.channels,
                    [id]: {
                        ...action.payload,
                        messages: (state.channels[id] ? state.channels[id].messages : messages) ?? [],
                    },
                },
            };
        }

        case MESSAGING.SET_CHANNEL_MESSAGE_LIST: {
            const { channelId, list } = action.payload ?? {};
            return {
                ...state,
                channels: {
                    ...state.channels,
                    ...(state.channels[channelId]
                        ? {
                              [channelId]: {
                                  ...state.channels[channelId],
                                  messages: list.filter(filterChannelMessages),
                              },
                          }
                        : {
                              [channelId]: {
                                  messages: list.filter(filterChannelMessages),
                                  id: channelId,
                                  name: '',
                                  lastMessage: '',
                                  lastMessageTimestamp: null,
                                  hasUnread: false,
                                  isLastMessageWithCallback: false,
                              },
                          }),
                },
            };
        }

        case MESSAGING.ADD_CHANNEL_MESSAGE: {
            const { channelId, id } = action.payload;

            if (!state.channels[channelId]) {
                return state;
            }

            const messages = state.channels[channelId]?.messages || [];
            const channelState = state.channels[channelId];
            const viewed = Math.floor(Date.now() / 1000);
            const isPendingMessage = id?.startsWith('pending-');
            const isConfirmedCurrentUserMessage = action.payload.isCurrentUser && !isPendingMessage;

            const messagesWithoutPending = isConfirmedCurrentUserMessage
                ? messages.filter((item) => !item.id?.startsWith('pending-'))
                : messages;

            return {
                ...state,
                channels: {
                    ...state.channels,
                    [channelId]: {
                        ...channelState,
                        messages: messagesWithoutPending.find((item) => item.id === id)
                            ? messagesWithoutPending.map((item) => (item.id === id ? { ...action.payload } : item))
                            : [...messagesWithoutPending, action.payload],
                        viewed,
                        lastMessageTimestamp: action.payload?.timestamp || viewed,
                        lastMessage: action.payload?.text || '',
                        isLastMessageWithCallback: Boolean(action.payload?.callback),
                    },
                },
            };
        }

        case MESSAGING.REMOVE_CHANNEL_MESSAGE: {
            const { channelId, messageId } = action.payload;

            if (!state.channels[channelId]) {
                return state;
            }

            return {
                ...state,
                channels: {
                    ...state.channels,
                    [channelId]: {
                        ...state.channels[channelId],
                        messages: state.channels[channelId].messages.filter((item) => item.id !== messageId),
                    },
                },
            };
        }

        case MESSAGING.CHANNEL_VIEWED: {
            const channelId = action.payload;

            // Current moment in seconds
            const viewed = Math.floor(Date.now() / 1000);

            return {
                ...state,
                channels: {
                    ...state.channels,
                    [channelId]: { ...state.channels[channelId], viewed },
                },
            };
        }

        case MESSAGING.SET_HAS_UNREAD_MESSAGES: {
            return {
                ...state,
                hasUnreadMessages: action.payload,
            };
        }

        case AUTH.LOGOUT: {
            return { ...initState };
        }

        case MESSAGING.SET_CHANNELS_MAP: {
            const updatedMapByAgreement = { ...state.channelsMapByAgreement };

            if (action.payload.selectedAgreementId) {
                updatedMapByAgreement[action.payload.selectedAgreementId] = action.payload.channelsMap;
            }

            return {
                ...state,
                channels: generateChannelsFromMap(action.payload.channelsMap, state.channels),
                channelsInLoad: false,
                channelsMap: action.payload.channelsMap,
                channelsMapByAgreement: updatedMapByAgreement,
            };
        }

        case MESSAGING.NEW_CHAT_CREATED: {
            return { ...state, newChatCreated: action.payload };
        }

        case MESSAGING.GET_CHANNELS_MAP: {
            return { ...state, channelsInLoad: true };
        }

        case MESSAGING.FETCHED_NEW_CHANNELS_MAP: {
            return { ...state, channelsInLoad: false };
        }

        case MESSAGING.FETCHED_ASSISTANT_CONVERSATION: {
            return {
                ...state,
                assistantConversationId: action.payload.data.assistantConversation.id,
                assistantConversationName: action.payload.data.assistantConversation.type,
                assistantConversationMuted: Boolean(action.payload.data.assistantConversation.muted),
            };
        }

        case MESSAGING.SET_ASSISTANT_CONVERSATION_MUTED: {
            return {
                ...state,
                assistantConversationMuted: action.payload,
            };
        }

        default:
            return state;
    }
};
